import { Injectable } from '@angular/core';
import { AuthSession, JwtClaims, SocialLoginDto } from '../../features/auth/domain/models/social-registration.model';

/**
 * Sesión del pasajero durante la pestaña actual. El JWT se decodifica sólo para
 * leer sus claims en la interfaz; la autorización real siempre corresponde al backend.
 */
@Injectable({ providedIn: 'root' })
export class AuthSessionService {
  private static readonly STORAGE_KEY = 'ittiva.auth.session';

  save(login: SocialLoginDto): AuthSession {
    const session: AuthSession = {
      tokenJWT: login.tokenJWT,
      claims: this.decodeJwt(login.tokenJWT),
      user: {
        nombreCompleto: login.nombreCompleto,
        roles: login.roles,
        username: login.username,
      },
    };

    this.storage.setItem(AuthSessionService.STORAGE_KEY, JSON.stringify(session));
    return session;
  }

  /** Reemplaza solamente el JWT tras una renovación y conserva los datos del usuario. */
  updateToken(tokenJWT: string): AuthSession {
    const currentSession = this.read();
    if (!currentSession) {
      throw new Error('No hay una sesión activa para actualizar el token.');
    }

    const session: AuthSession = {
      ...currentSession,
      tokenJWT,
      claims: this.decodeJwt(tokenJWT),
    };

    this.storage.setItem(AuthSessionService.STORAGE_KEY, JSON.stringify(session));
    return session;
  }

  read(): AuthSession | null {
    const rawSession = this.storage.getItem(AuthSessionService.STORAGE_KEY);
    if (!rawSession) {
      return null;
    }

    try {
      const session = JSON.parse(rawSession) as AuthSession;
      if (!this.isSession(session) || this.isExpired(session)) {
        this.clear();
        return null;
      }

      return session;
    } catch {
      this.clear();
      return null;
    }
  }

  token(): string | null {
    return this.read()?.tokenJWT ?? null;
  }

  claims(): JwtClaims | null {
    return this.read()?.claims ?? null;
  }

  isAuthenticated(): boolean {
    const session = this.read();
    if (!session) {
      return false;
    }

    return !this.isExpired(session);
  }

  clear(): void {
    this.storage.removeItem(AuthSessionService.STORAGE_KEY);
  }

  private decodeJwt(token: string): JwtClaims {
    const payload = token.split('.')[1];
    if (!payload) {
      throw new Error('El servidor devolvió un JWT inválido.');
    }

    try {
      const normalizedPayload = payload.replace(/-/g, '+').replace(/_/g, '/');
      const paddedPayload = normalizedPayload.padEnd(Math.ceil(normalizedPayload.length / 4) * 4, '=');
      const bytes = Uint8Array.from(atob(paddedPayload), (character) => character.charCodeAt(0));
      const decoded = new TextDecoder().decode(bytes);
      const claims = JSON.parse(decoded) as unknown;

      if (!claims || typeof claims !== 'object' || Array.isArray(claims)) {
        throw new Error('El JWT no contiene claims válidos.');
      }

      return claims as JwtClaims;
    } catch {
      throw new Error('No fue posible leer el JWT de sesión.');
    }
  }

  private isSession(value: AuthSession): boolean {
    return Boolean(
      value &&
        typeof value.tokenJWT === 'string' &&
        value.claims &&
        typeof value.claims === 'object' &&
        value.user &&
        typeof value.user.username === 'string',
    );
  }

  private isExpired(session: AuthSession): boolean {
    const expiresAt = session.claims.exp;
    return typeof expiresAt === 'number' && expiresAt * 1000 <= Date.now();
  }

  private get storage(): Storage {
    return sessionStorage;
  }
}
